/* eslint-disable no-mixed-spaces-and-tabs */
import { IonIcon } from '@ionic/react'
import { ConsumptionTimeFrame, Report } from '@vae/server/dist/client'
import { arrowDown, arrowUp } from 'ionicons/icons'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
	observedReport: Report
	previousReport: Report | null | undefined
	averages:
		| {
				id: number
				personCount: number
				from: string
				to: string
				totalConsumption: number
				totalCO2: number
				heatingCO2: number
				timeframe: ConsumptionTimeFrame
				totalHeatingConsumption: number
		  }
		| null
		| undefined
}

const General: React.FC<Props> = ({ observedReport, previousReport, averages }) => {
	const { t } = useTranslation('pages_monthlyReport')

	const values = useMemo(() => {
		const consumptionCompareAgainst =
			previousReport?.totalConsumption ?? averages?.totalConsumption ?? 0
		const billCompareAgainst = previousReport?.totalBill ?? 0
		const co2CompareAgainst = previousReport?.totalCO2 ?? averages?.totalCO2 ?? 0

		return {
			consumption: Math.round(observedReport.totalConsumption),
			consumptionDifference: Math.abs(
				Number((observedReport.totalConsumption - consumptionCompareAgainst).toFixed(2))
			),
			bill: observedReport.totalBill.toFixed(2),
			billDifference: Math.abs(Number((observedReport.totalBill - billCompareAgainst).toFixed(2))),
			lastMonthBill: previousReport?.totalBill ?? 0,
			co2: Math.round(observedReport.totalCO2),
			co2Difference: Math.abs(Number((observedReport.totalCO2 - co2CompareAgainst).toFixed(2))),

			isBillHigher: observedReport.totalConsumption > consumptionCompareAgainst,
			isConsumptionHigher: observedReport.totalConsumption > consumptionCompareAgainst,
			isCo2Higher: observedReport.totalCO2 > co2CompareAgainst,
		}
	}, [observedReport, previousReport, averages])

	const generateDescription = (
		isHigherThanPrevious: boolean,
		difference: number,
		prevReport: Report | null | undefined,
		avg: Props['averages']
	) => {
		if (prevReport) {
			if (difference === 0) {
				return ` ${t('samaKuin')}` // Same month, one year ago
			}

			return isHigherThanPrevious ? ` ${t('enemmanKuin')}` : ` ${t('vahemmanKuin')}`
		} else if (avg) {
			const householdSize = avg.personCount
			return isHigherThanPrevious
				? ` ${t('keskivertoaEnemman')} ${t('muissa')} ${householdSize} ${t('talouksissa')}` // More than average
				: ` ${t('keskivertoaVahemman')} ${t('muissa')} ${householdSize} ${t('talouksissa')}` // Less than average
		}
		return ` `
	}

	return (
		<>
			<div className='cards'>
				<div className='top bg-brand-secondary-200-light dark:bg-brand-secondary-100-dark'>
					<div className='card-info text-text-dark'>
						<div style={{ color: values.isConsumptionHigher ? '#b21672' : '#52ae32' }}>
							<IonIcon icon={values.isConsumptionHigher ? arrowUp : arrowDown} />
							{values.consumptionDifference} kWh
						</div>
						{!previousReport && !averages ? (
							<div>{t('puuttuvaVertailu')}</div>
						) : (
							<div className='description'>
								{t('sahkonKulumista')}
								{generateDescription(
									values.isConsumptionHigher,
									values.consumptionDifference,
									previousReport,
									averages
								)}
							</div>
						)}
					</div>
				</div>
				<div className='bottom'>
					<div className='card-info bg-brand-secondary-200-light dark:bg-brand-secondary-100-dark text-text-dark'>
						<div style={{ color: values.isBillHigher ? '#b21672' : '#52ae32' }}>
							<IonIcon icon={values.isBillHigher ? arrowUp : arrowDown} />
							{values.billDifference} €
						</div>
						<div className='description'>
							{t('rahanKulumista')}
							{generateDescription(
								values.isBillHigher,
								values.billDifference,
								previousReport,
								averages
							)}
						</div>
					</div>
					<div className='card-info bg-brand-secondary-200-light dark:bg-brand-secondary-100-dark text-text-dark'>
						<div style={{ color: values.isCo2Higher ? '#b21672' : '#52ae32' }}>
							<IonIcon icon={values.isCo2Higher ? arrowUp : arrowDown} />
							{values.co2Difference} kg / CO2
						</div>
						<div className='description'>
							{t('co2')}
							{generateDescription(
								values.isCo2Higher,
								values.co2Difference,
								previousReport,
								averages
							)}
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

export default General
